<template>
  <!-- prettier-ignore -->
  <ovp-screen
    class="screen"
    :config="screenConfig">
    <ovp-dap-card class="card">
      <template #title>
        {{ $t('screen.reentry.resume') }}
      </template>

      <ovp-message
        severity="info"
        :text="$t('screen.reentry.tanHelptext', { mobile: basket.mobile })"
      />

      <ovp-form
        class="form"
        autocomplete="off"
        name="reentry"
        tracking-disabled
        :validator="$v.form"
        @submit="submit">
        <ovp-code-input
          v-model="form.code"
          numeric
          required
          class="form__input"
          name="code"
          theme="dark"
          :title="$t('screen.reentry.insertTan')"
          :validate="$v.form.code"
        />

        <div class="form-actions">
          <ovp-button
            plain
            :disabled="smsDisabled"
            @click="sendTAN">
            {{ $t('screen.reentry.resend') }}
          </ovp-button>

          <ovp-button
            primary
            type="submit">
            {{ $t('form.next') }}
          </ovp-button>
        </div>
      </ovp-form>
    </ovp-dap-card>

    <div class="screen-actions">
      <ovp-button
        secondary
        @click="restart">
        {{ $t('screen.reentry.restart') }}
      </ovp-button>
    </div>
  </ovp-screen>
</template>

<script>
import { mapState } from 'vuex'
import { isNil } from 'lodash'

import { minLength, required } from 'vuelidate/lib/validators'

import OvpMessage from '@/components/Container/Message'
import OvpDapCard from '@/components/Container/DapCard'
import OvpScreen from '@/components/Container/Screen'

import BasketMixin from '@/mixins/Basket'

import { BasketHelper, CollectiveHelper, StatusHelper } from '@/helpers'
import { ContractHelper } from '@/helpers/Contract'
import { ROUTE_NAME_NEXT_STEPS, ROUTE_NAME_PERSONS } from '@/config/constants'

export default {
  name: 'ReEntry',

  mixins: [BasketMixin],

  components: {
    OvpDapCard,
    OvpScreen,
    OvpMessage,
  },

  props: {
    token: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      form: {
        code: null,
      },
      screenConfig: {
        header: {
          light: true,
        },
      },
      smsDisabled: false,
    }
  },

  validations: {
    form: {
      code: {
        required,
        minLength: minLength(4),
      },
    },
  },

  computed: {
    ...mapState(['basket']),
  },

  methods: {
    restart() {
      this.$session.clear().then(() => {
        this.$router.replace({ name: ROUTE_NAME_PERSONS })
      })
    },

    sendTAN() {
      this.$_getBasket(this.token).then(() => {
        this.$notification.add(this.$t('screen.auth.sent'))
      })
    },

    updateReentryBasket(basket) {
      if (basket.submitted || !StatusHelper.isPersonalDataValid(basket.persons)) {
        return BasketHelper.updateBasketSession(basket)
          .then(basket => {
            return CollectiveHelper.loadCollective(basket)
          })
          .then(basket => BasketHelper.getAndSetProducts(basket))
      }
      return this.updateBasket(basket)
    },

    submit() {
      this.$loading.start()
      const payload = {
        basketId: this.basket.basketId,
        tan: this.form.code,
        token: this.token,
      }

      this.$backend.ovp
        .loadBasket(payload)
        .then(basket => BasketHelper.checkConsistency(basket))
        .then(basket => BasketHelper.setLanguage(basket))
        .then(basket => this.updateReentryBasket(basket))
        .then(basket => BasketHelper.getAndSetDistributionPartnerByBasket(basket))
        .then(basket => ContractHelper.loadContract(basket))
        .then(basket => {
          if (basket.submitted) {
            this.$router.replace({ name: ROUTE_NAME_NEXT_STEPS })
          } else if (basket.signingProcessStarted || !isNil(basket.cancellationAnswer)) {
            return BasketHelper.updateBasket({ signingProcessStarted: false })
              .then(basket => this.updateBasket(basket))
              .then(() => this.$router.replace({ name: 'submit' }))
          } else {
            this.$router.replace({ name: 'basket' })
          }
        })
        .catch(() => {
          this.form.code = null
        })
        .finally(() => {
          this.$loading.end()
        })
    },

    $_getBasket(token) {
      this.smsDisabled = true
      this.$loading.start()

      return this.$backend.ovp
        .sendSMSTan(token)
        .then(basket => (this.basket = basket))
        .finally(() => {
          this.$loading.end()

          // retrying for a tan is disabled for 2
          window.setTimeout(() => (this.smsDisabled = false), 60 * 2 * 1000)
        })
    },
  },
}
</script>

<style name="mobile" scoped>
.screen {
  padding: 15px;

  &-actions {
    margin-top: 10px;
  }

  .form {
    &-actions {
      display: flex;
      flex-direction: column;
      margin-top: 10px;

      .plain {
        color: var(--c-original-green);
      }
    }

    &__input {
      margin-top: 15px;
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .screen {
    .form {
      &-actions {
        flex-direction: row;
        justify-content: space-between;
      }
    }
  }
}
</style>
